import {
  afterNextRender,
  AfterRenderRef,
  computed,
  effect,
  EffectRef,
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { GameId } from 'src/app/model/game/game-id';
import { UxGameController } from 'src/app/features/game/controllers/game-legacy/ux-game-controller';
import { GenericSceneController } from 'src/app/features/game/controllers/scene-controllers/scene-controller-factory';
import { GameInterfaceState } from 'src/app/features/game/controllers/listeners-legacy/ux-game-listener';
import { UxGameService } from '../ux-game/ux-game.service';
import { GameScoreService } from '../game-score/game-score.service';
import { JobService } from '../job/job.service';
import { ScrollStateService } from '../scroll-state/scroll-state.service';

export type ControlMode = 'full' | 'game-only';

export interface GameStateEvent {
  state: GameInterfaceState;
  controller: UxGameController | GenericSceneController;
  gameId: GameId;
}

@Injectable({
  providedIn: 'root',
})
export class GameOrchestrationService {
  private injector = inject(Injector);
  private uxGameService = inject(UxGameService);
  private gameScoreService = inject(GameScoreService);
  private jobService = inject(JobService);
  private scrollState = inject(ScrollStateService);

  private activeControllerSignal = signal<UxGameController | GenericSceneController | undefined>(
    undefined,
  );
  private gameIdSignal = signal<GameId | undefined>(undefined);
  private gameInterfaceStateSignal = signal<GameInterfaceState>(GameInterfaceState.GameLoading);

  /** Emits each time the game interface state changes. Consumers subscribe to drive control display logic. */
  private stateEvent$ = new Subject<GameStateEvent>();
  private destroy$ = new Subject<void>();
  private cleanupEffects: EffectRef[] = [];

  readonly activeController = this.activeControllerSignal.asReadonly();
  readonly gameId = this.gameIdSignal.asReadonly();
  readonly gameInterfaceState = this.gameInterfaceStateSignal.asReadonly();
  readonly isActive = computed(() => this.activeControllerSignal() !== undefined);

  private pendingVisibilityRef: AfterRenderRef | null = null;

  private visibilityEffect = effect(() => {
    const controller = this.activeControllerSignal();
    const isGameRoute = this.scrollState.isGameRoute();

    this.pendingVisibilityRef?.destroy();
    this.pendingVisibilityRef = null;

    if (controller) {
      if (isGameRoute) {
        // Defer showing until after Angular finishes rendering (game-hidden class removed)
        this.pendingVisibilityRef = afterNextRender(
          () => {
            this.pendingVisibilityRef = null;
            controller.setGameVisible(true);
          },
          { injector: this.injector },
        );
      } else {
        controller.setGameVisible(false);
      }
    }
  });

  get stateEvents$() {
    return this.stateEvent$.asObservable();
  }

  async activate(gameId: GameId): Promise<void> {
    if (this.isActive() && this.gameIdSignal() === gameId) {
      return;
    }

    if (this.isActive()) {
      this.deactivate();
    }

    const job = this.jobService.create(async () => this.uxGameService.createGame(gameId));
    job.addErrorHandler().showFatalError().reloadWindow();
    const controller = await job.runNoThrow();

    if (controller) {
      this.setupGameScoreSync(controller);
      this.gameIdSignal.set(gameId);
      this.activeControllerSignal.set(controller);
      this.subscribeToStateChanges(gameId, controller);
    }
  }

  deactivate(): void {
    this.destroy$.next();

    this.cleanupEffects.forEach((ref) => ref.destroy());
    this.cleanupEffects = [];

    this.activeControllerSignal()?.onDestroy();
    this.activeControllerSignal.set(undefined);
    this.gameIdSignal.set(undefined);
    this.gameInterfaceStateSignal.set(GameInterfaceState.GameLoading);
  }

  private subscribeToStateChanges(
    gameId: GameId,
    controller: UxGameController | GenericSceneController,
  ): void {
    if (controller instanceof UxGameController) {
      runInInjectionContext(this.injector, () => {
        toObservable(controller.gameInterfaceStateSignal)
          .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe((state) => {
            this.handleStateChange(state, gameId, controller);
          });
      });
    } else {
      runInInjectionContext(this.injector, () => {
        toObservable(controller.gameplayInterfaceStateSignal)
          .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe((state) => {
            this.handleStateChange(state, gameId, controller);
          });
      });
    }
  }

  private handleStateChange(
    state: GameInterfaceState,
    gameId: GameId,
    controller: UxGameController | GenericSceneController,
  ): void {
    this.gameInterfaceStateSignal.set(state);
    this.stateEvent$.next({ state, controller, gameId });
  }

  private setupGameScoreSync(controller: UxGameController | GenericSceneController): void {
    this.cleanupEffects.forEach((ref) => ref.destroy());
    this.cleanupEffects = [];

    const opts = { allowSignalWrites: true };
    if (controller instanceof UxGameController) {
      runInInjectionContext(this.injector, () => {
        this.cleanupEffects.push(
          effect(() => {
            this.gameScoreService.setGameInterfaceState(controller.gameInterfaceStateSignal());
          }, opts),
          effect(() => {
            this.gameScoreService.setScore(controller.scoreSignal());
          }, opts),
          effect(() => {
            const countdown = controller.countdownSignal();
            if (countdown !== undefined) {
              this.gameScoreService.setCountdown(countdown);
            }
          }, opts),
          effect(() => {
            this.gameScoreService.setFormattedReward(controller.formattedRewardSignal());
          }, opts),
          effect(() => {
            this.gameScoreService.setTournamentId(controller.tournamentIdSignal());
          }, opts),
          effect(() => {
            this.gameScoreService.setGameBehaviorType(controller.gameBehaviorSignal());
          }, opts),
        );
      });
    } else {
      runInInjectionContext(this.injector, () => {
        this.cleanupEffects.push(
          effect(() => {
            this.gameInterfaceStateSignal.set(controller.gameplayInterfaceStateSignal());
            this.gameScoreService.setGameInterfaceState(controller.gameplayInterfaceStateSignal());
          }, opts),
          effect(() => {
            this.gameScoreService.setScore(controller.gameScoreSignals.currentScore());
          }, opts),
          effect(() => {
            const countdown = controller.gameScoreSignals.countdown();
            if (countdown !== undefined) {
              this.gameScoreService.setCountdown(countdown);
            }
          }, opts),
          effect(() => {
            this.gameScoreService.setFormattedReward(controller.gameScoreSignals.formattedReward());
          }, opts),
          effect(() => {
            this.gameScoreService.setTournamentId(controller.gameScoreSignals.tournamentId());
          }, opts),
          effect(() => {
            this.gameScoreService.setRoomId(controller.gameScoreSignals.roomId());
          }, opts),
          effect(() => {
            this.gameScoreService.setGameBehaviorType(controller.currentGameBehavior());
          }, opts),
        );
      });
    }
  }
}
